import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent, useMemo } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select } from '../components/Field.js'
import { Money } from '../components/Money.js'
import { splitIntoTerms, type TermDraft } from '../lib/installments.js'

export const Route = createFileRoute('/_app/fees/new')({
  component: NewInvoicePage,
})

interface StudentRow {
  id: string
  fullName: string
  className: string | null
  classes?: { id: string; name: string }[]
  isActive: boolean
}

interface CategoryRow {
  id: string
  name: string
  amountInr: number
  termCount: number
  isActive: boolean
}

interface ClassFeeRow {
  id: string
  classId: string
  feeCategoryId: string
  amountInr: number
}

function defaultDueDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return d.toISOString().slice(0, 10)
}

function NewInvoicePage() {
  const navigate = useNavigate()

  const students = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api<StudentRow[]>('/api/v1/students')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const categories = useQuery({
    queryKey: ['fee-categories'],
    queryFn: async () => {
      const res = await api<CategoryRow[]>('/api/v1/fee-categories')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const classFees = useQuery({
    queryKey: ['class-fees'],
    queryFn: async () => {
      const res = await api<ClassFeeRow[]>('/api/v1/class-fees')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [studentId, setStudentId] = useState('')
  const [classId, setClassId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [amountRupees, setAmountRupees] = useState('')
  const [discountRupees, setDiscountRupees] = useState('')
  const [dueDate, setDueDate] = useState(() => defaultDueDate())
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Per-term installment rows. Empty unless a multi-term category is selected.
  // Each term carries its own amount (₹) + due date; the admin can edit both.
  const [installments, setInstallments] = useState<TermDraft[]>([])
  const isInstalment = installments.length > 0

  // The classes the selected student is enrolled in (for the class picker).
  const studentClasses = useMemo(
    () => students.data?.find((s) => s.id === studentId)?.classes ?? [],
    [students.data, studentId],
  )

  // Standard amount (paise) for a category, preferring a per-class fee for the
  // chosen class, falling back to the category's academy-wide amount.
  const standardAmountInr = (catId: string, clsId: string): number | undefined => {
    const cat = catId ? categories.data?.find((c) => c.id === catId) : undefined
    if (!cat) return undefined
    const classFee =
      clsId && classFees.data?.find((f) => f.classId === clsId && f.feeCategoryId === catId)
    return classFee ? classFee.amountInr : cat.amountInr
  }

  // Seed amount + installments from the resolved standard, but let admin override.
  // When the category splits into terms, seed one installment row per term — the
  // gross is divided evenly (remainder on term 1) and due dates step monthly.
  const applyStandard = (catId: string, clsId: string) => {
    const cat = catId ? categories.data?.find((c) => c.id === catId) : undefined
    const amountInr = standardAmountInr(catId, clsId)
    if (!cat || amountInr === undefined) {
      setAmountRupees('')
      setInstallments([])
      return
    }
    setAmountRupees((amountInr / 100).toString())
    if (cat.termCount > 1) {
      setInstallments(splitIntoTerms(amountInr, cat.termCount, dueDate))
    } else {
      setInstallments([])
    }
  }

  const onCategoryChange = (id: string) => {
    setCategoryId(id)
    applyStandard(id, classId)
  }

  const onClassChange = (id: string) => {
    setClassId(id)
    applyStandard(categoryId, id)
  }

  // Reset class when the student changes (their class options differ).
  const onStudentChange = (id: string) => {
    setStudentId(id)
    setClassId('')
    applyStandard(categoryId, '')
  }

  const amountPaise = Math.round((parseFloat(amountRupees) || 0) * 100)
  const discountPaise = Math.round((parseFloat(discountRupees) || 0) * 100)
  const finalPaise = Math.max(0, amountPaise - discountPaise)

  const installmentTotalPaise = installments.reduce(
    (sum, t) => sum + Math.round((parseFloat(t.amountRupees) || 0) * 100),
    0,
  )
  const installmentsValid =
    !isInstalment ||
    installments.every(
      (t) => t.dueDate && Math.round((parseFloat(t.amountRupees) || 0) * 100) > 0,
    )

  const updateTerm = (index: number, patch: Partial<TermDraft>) => {
    setInstallments((prev) =>
      prev.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    )
  }

  const create = useMutation({
    mutationFn: async () => {
      const body = isInstalment
        ? {
            studentId,
            feeCategoryId: categoryId || null,
            // amountInr/dueDate are ignored server-side when installments are
            // present, but the schema still requires them — send the gross.
            amountInr: installmentTotalPaise,
            dueDate: installments[0]?.dueDate ?? dueDate,
            notes: notes || undefined,
            installments: installments.map((t, i) => ({
              amountInr: Math.round((parseFloat(t.amountRupees) || 0) * 100),
              discountInr: 0,
              dueDate: t.dueDate,
              label: `Term ${i + 1} of ${installments.length}`,
            })),
          }
        : {
            studentId,
            feeCategoryId: categoryId || null,
            amountInr: amountPaise,
            discountInr: discountPaise,
            dueDate,
            notes: notes || undefined,
          }

      const res = await api<{ id: string }>('/api/v1/fee-invoices', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => navigate({ to: '/fees' }),
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    create.mutate()
  }

  const activeStudents = useMemo(
    () => students.data?.filter((s) => s.isActive) ?? [],
    [students.data],
  )

  return (
    <div className="max-w-3xl space-y-8">
      <PageHeader
        title="New invoice"
        description="Raise a fee for one student. The primary parent will be notified."
      />

      <form onSubmit={onSubmit} className="space-y-6">
        <Card>
          <CardHeader title="Invoice" />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Student" required>
                <Select
                  value={studentId}
                  onChange={(e) => onStudentChange(e.target.value)}
                  required
                >
                  <option value="">Select a student…</option>
                  {activeStudents.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.fullName}
                      {s.className ? ` · ${s.className}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Class"
                optional
                hint="Picks the standard fee for this class, if set."
              >
                <Select
                  value={classId}
                  onChange={(e) => onClassChange(e.target.value)}
                  disabled={!studentId || studentClasses.length === 0}
                >
                  <option value="">No class</option>
                  {studentClasses.map((cl) => (
                    <option key={cl.id} value={cl.id}>
                      {cl.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Fee category" optional>
                <Select
                  value={categoryId}
                  onChange={(e) => onCategoryChange(e.target.value)}
                >
                  <option value="">Custom (no template)</option>
                  {categories.data
                    ?.filter((c) => c.isActive)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · ₹{(c.amountInr / 100).toLocaleString('en-IN')}
                      </option>
                    ))}
                </Select>
              </Field>
              {!isInstalment ? (
                <>
                  <Field label="Amount (₹)" required>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={amountRupees}
                      onChange={(e) => setAmountRupees(e.target.value)}
                      required
                      numeric
                      prefix="₹"
                    />
                  </Field>
                  <Field label="Discount (₹)" optional>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={discountRupees}
                      onChange={(e) => setDiscountRupees(e.target.value)}
                      numeric
                      prefix="₹"
                    />
                  </Field>
                  <Field label="Due date" required>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      required
                    />
                  </Field>
                </>
              ) : null}
              <Field label="Notes" optional>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reference, term, late fee waiver…"
                />
              </Field>
            </div>

            {isInstalment ? (
              <div className="space-y-3 pt-2">
                <p className="text-sm font-medium text-fg">
                  Installments
                  <span className="ml-2 font-normal text-fg-subtle">
                    One invoice is created per term.
                  </span>
                </p>
                {installments.map((term, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4 rounded-md border border-border p-3"
                  >
                    <Field label={`Term ${i + 1} amount (₹)`} required>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={term.amountRupees}
                        onChange={(e) => updateTerm(i, { amountRupees: e.target.value })}
                        required
                        numeric
                        prefix="₹"
                      />
                    </Field>
                    <Field label={`Term ${i + 1} due date`} required>
                      <Input
                        type="date"
                        value={term.dueDate}
                        onChange={(e) => updateTerm(i, { dueDate: e.target.value })}
                        required
                      />
                    </Field>
                  </div>
                ))}
              </div>
            ) : null}
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-fg-subtle">
                {isInstalment ? 'Total across terms:' : 'Final amount:'}
              </span>{' '}
              <Money
                paise={isInstalment ? installmentTotalPaise : finalPaise}
                className="text-base font-semibold text-fg"
              />
            </div>
            <div className="flex items-center gap-3">
              {error ? <span className="text-sm text-danger">{error}</span> : null}
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate({ to: '/fees' })}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={create.isPending}
                disabled={
                  !studentId ||
                  (isInstalment
                    ? !installmentsValid || installmentTotalPaise <= 0
                    : !amountRupees || finalPaise <= 0)
                }
              >
                {isInstalment
                  ? `Create ${installments.length} invoices`
                  : 'Create invoice'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}
